import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CircleSlash, Repeat, RotateCcw, WifiOff } from 'lucide-react';
import { Skeleton } from '@/components/ui';
import { getSetting, SETTING_KEYS, setSetting } from '@/db/repositories/settings';
import { useReachability } from '@/hooks/useOnlineStatus';
import type { MediaResolver } from '@/services/media/MediaResolver';
import type { PlaybackPlan } from '@/services/media/types';
import type { Sign } from '@/types/catalog';

/** Velocidades previstas no PLAN §17. Nada acima de 1x: o objetivo é estudar o sinal. */
const SPEEDS = [0.5, 0.75, 1] as const;
type Speed = (typeof SPEEDS)[number];

interface Props {
  sign: Sign;
  resolver: MediaResolver;
  /** Chamado quando o arquivo local se mostra ilegível, para a UI reagir. */
  onLocalFileBroken?: (signId: string) => void;
}

/**
 * Player de vídeo (PLAN §17).
 *
 * Um componente só para offline e online — a diferença fica no `playbackPlan`, não
 * em telas separadas. Controles de estudo (velocidade, repetição, replay) só fazem
 * sentido sobre `<video>`; o iframe do YouTube tem os próprios.
 */
export function VideoPlayer({ sign, resolver, onLocalFileBroken }: Props): React.ReactElement {
  const [plan, setPlan] = useState<PlaybackPlan | null>(null);
  const [broken, setBroken] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [loop, setLoop] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  // Embed e MP4 remoto dependem de rede; o arquivo no dispositivo não. A sonda só
  // roda nos dois primeiros casos, para não gastar requisição quando o vídeo já
  // está local.
  const needsNetwork = plan?.kind === 'embed' || plan?.kind === 'direct';
  const reachable = useReachability(needsNetwork);

  // Preferências de estudo são do usuário, não do sinal: persistem entre vídeos.
  useEffect(() => {
    void getSetting<Speed>(SETTING_KEYS.playbackRate, 1).then((stored) => {
      if (SPEEDS.includes(stored)) setSpeed(stored);
    });
    void getSetting<boolean>(SETTING_KEYS.loopVideo, false).then(setLoop);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let created: string | undefined;

    setPlan(null);
    setBroken(false);

    void resolver.playbackPlan(sign).then((next) => {
      if (cancelled) {
        // O componente saiu antes da resolução: libera já para não vazar.
        if (next.isObjectUrl && next.url) URL.revokeObjectURL(next.url);
        return;
      }
      if (next.isObjectUrl) created = next.url;
      setPlan(next);
    });

    return () => {
      cancelled = true;
      // Sem isto, cada troca de sinal retém o blob anterior em memória (PLAN §35).
      if (created) URL.revokeObjectURL(created);
    };
  }, [sign, resolver]);

  // `playbackRate` é propriedade do elemento, não atributo: precisa ser reaplicada
  // a cada nova fonte, senão o vídeo seguinte volta a 1x.
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed, plan]);

  const changeSpeed = useCallback((next: Speed) => {
    setSpeed(next);
    void setSetting(SETTING_KEYS.playbackRate, next);
  }, []);

  const toggleLoop = useCallback(() => {
    setLoop((current) => {
      const next = !current;
      void setSetting(SETTING_KEYS.loopVideo, next);
      return next;
    });
  }, []);

  /** Replay rápido: volta ao início e toca de novo (F11-T08). */
  const replay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    void video.play().catch(() => undefined);
  }, []);

  if (!plan) return <Skeleton className="aspect-video w-full" />;

  if (plan.kind === 'unavailable') {
    return (
      <MessageBox icon={<CircleSlash size={28} aria-hidden />} text={plan.reason ?? 'Sem vídeo.'} />
    );
  }

  // Falha de leitura. A causa depende da origem: arquivo local corrompido pede
  // rebaixar; URL remota que não carregou é problema de rede ou de origem. Dar a
  // mensagem errada mandaria o usuário consertar a coisa errada.
  if (broken) {
    return (
      <MessageBox
        icon={<AlertTriangle size={28} aria-hidden />}
        text={
          plan.kind === 'offline'
            ? 'O arquivo salvo neste dispositivo está corrompido. Remova-o e baixe de novo.'
            : 'Não foi possível carregar o vídeo. Verifique a conexão e tente de novo.'
        }
        tone="warning"
      />
    );
  }

  if (needsNetwork) {
    // Enquanto a sonda não responde, não vale renderizar o player: sem rede, o
    // usuário veria um retângulo preto sem explicação.
    if (reachable === null) return <Skeleton className="aspect-video w-full" />;

    if (!reachable) {
      return (
        <MessageBox
          icon={<WifiOff size={28} aria-hidden />}
          text="Este sinal ainda não está disponível offline e precisa de conexão para tocar."
        />
      );
    }
  }

  if (plan.kind === 'embed') {
    return (
      <iframe
        key={plan.url}
        src={plan.url}
        title={`Vídeo do sinal ${sign.name}`}
        className="aspect-video w-full rounded-xl bg-black"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <video
        ref={videoRef}
        key={plan.url}
        src={plan.url}
        className="aspect-video w-full rounded-xl bg-black"
        controls
        loop={loop}
        // Sem playsInline o iOS sequestra o vídeo para tela cheia ao dar play.
        playsInline
        preload="metadata"
        // Nunca autoplay com som: exige gesto do usuário (F11-T12).
        aria-label={`Vídeo do sinal ${sign.name}`}
        onError={() => {
          setBroken(true);
          onLocalFileBroken?.(sign.id);
        }}
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={replay}
          className="flex min-h-11 items-center gap-1.5 rounded-lg bg-slate-800 px-3 text-sm active:bg-slate-700"
        >
          <RotateCcw size={16} aria-hidden />
          Repetir
        </button>

        <button
          type="button"
          onClick={toggleLoop}
          aria-pressed={loop}
          className={`flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-sm ${
            loop ? 'bg-sky-500/20 text-sky-300' : 'bg-slate-800 text-slate-300 active:bg-slate-700'
          }`}
        >
          <Repeat size={16} aria-hidden />
          Em loop
        </button>

        <fieldset className="ml-auto flex items-center gap-1">
          <legend className="sr-only">Velocidade de reprodução</legend>
          {SPEEDS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => changeSpeed(option)}
              aria-pressed={speed === option}
              className={`min-h-11 min-w-11 rounded-lg px-2 text-sm tabular-nums ${
                speed === option
                  ? 'bg-sky-500/20 text-sky-300'
                  : 'bg-slate-800 text-slate-400 active:bg-slate-700'
              }`}
            >
              {option}×
            </button>
          ))}
        </fieldset>
      </div>
    </div>
  );
}

function MessageBox({
  icon,
  text,
  tone = 'neutral',
}: {
  icon: React.ReactNode;
  text: string;
  tone?: 'neutral' | 'warning';
}): React.ReactElement {
  return (
    <div
      role={tone === 'warning' ? 'alert' : undefined}
      className={`flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl text-center ${
        tone === 'warning' ? 'bg-amber-500/10 text-amber-200' : 'bg-slate-800 text-slate-400'
      }`}
    >
      <span className={tone === 'warning' ? 'text-amber-400' : 'text-slate-600'}>{icon}</span>
      <p className="px-6 text-sm">{text}</p>
    </div>
  );
}
